/**
 * Merging two keyset-ordered timeline result sets (newest first).
 *
 * The account filter runs as TWO single-sided queries (money out of the account, money into
 * it) so each can be driven by an index; an `or()` across the two join-derived columns
 * forces the database to walk the household timeline instead. Each side returns its own top
 * rows in (occurred_at desc, id desc) order — any row in the union's top K is in its own
 * side's top K, so merging the two lists reproduces the union's order exactly.
 */

export type KeysetRow = Readonly<{
  line_id: string | null;
  id: string | null;
  occurred_at: string | null;
}>;

/**
 * PostgREST serializes timestamptz as UTC ISO strings, where lexicographic order IS
 * chronological order — including trimmed fractional seconds ('.5' vs '.25': the digit
 * comparison decides, and a missing digit sorts against '+'/'.' which rank below '0').
 * uuid text in canonical form also sorts like the database sorts uuid. Both facts keep this
 * comparison identical to the `order by occurred_at desc, id desc, line_id desc` the server
 * used — the line id is the tiebreak now that one entry can produce several rows.
 */
function compareDesc(x: KeysetRow, y: KeysetRow): number {
  const xOccurred = x.occurred_at ?? '';
  const yOccurred = y.occurred_at ?? '';
  if (xOccurred !== yOccurred) return xOccurred > yOccurred ? -1 : 1;
  const xId = x.id ?? '';
  const yId = y.id ?? '';
  if (xId !== yId) return xId > yId ? -1 : 1;
  const xLine = x.line_id ?? '';
  const yLine = y.line_id ?? '';
  if (xLine !== yLine) return xLine > yLine ? -1 : 1;
  return 0;
}

/**
 * Merges two lists that are each sorted newest-first, dropping duplicate LINES (a transfer
 * matches both sides and must appear once), and stops after `take` rows.
 */
export function mergeKeysetDesc<T extends KeysetRow>(
  a: readonly T[],
  b: readonly T[],
  take: number,
): T[] {
  const merged: T[] = [];
  const seen = new Set<string>();
  let i = 0;
  let j = 0;

  while (merged.length < take && (i < a.length || j < b.length)) {
    let pick: T;
    if (i >= a.length) {
      pick = b[j++]!;
    } else if (j >= b.length) {
      pick = a[i++]!;
    } else {
      pick = compareDesc(a[i]!, b[j]!) <= 0 ? a[i++]! : b[j++]!;
    }

    const key = pick.line_id ?? '';
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(pick);
  }

  return merged;
}
